import React, { useState, useEffect } from 'react';
import { customersAPI, invoicesAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../utils/helpers';
import { Plus, User } from '@phosphor-icons/react';

export const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [customerForm, setCustomerForm] = useState({
    name: '',
    mobile: '',
    email: '',
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await customersAPI.getAll();
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    try {
      await customersAPI.create(customerForm);
      toast.success('Customer added successfully');
      setDialogOpen(false);
      setCustomerForm({ name: '', mobile: '', email: '' });
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to add customer');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="text-[#6B7280]">Loading...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24 md:pb-8" data-testid="customers-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-['Chivo'] text-4xl md:text-5xl font-black tracking-tighter text-[#121212] mb-2">
              Customers
            </h1>
            <p className="text-[#6B7280] font-medium leading-relaxed">{customers.length} total customers</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-customer-button" className="bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full">
                <Plus className="h-4 w-4 mr-2" weight="bold" /> New Customer
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-customer-dialog">
              <DialogHeader>
                <DialogTitle>Add New Customer</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddCustomer} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" data-testid="customer-name-input" value={customerForm.name} onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mobile">Mobile</Label>
                  <Input id="mobile" data-testid="customer-mobile-input" value={customerForm.mobile} onChange={(e) => setCustomerForm({ ...customerForm, mobile: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email (Optional)</Label>
                  <Input id="email" data-testid="customer-email-input" type="email" value={customerForm.email} onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })} />
                </div>
                <Button type="submit" data-testid="submit-customer-button" className="w-full bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full">Add Customer</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {customers.length === 0 ? (
            <Card className="border-[#E5E7EB] bg-white col-span-full">
              <CardContent className="pt-6">
                <p className="text-center text-[#6B7280]">No customers yet. Add your first customer!</p>
              </CardContent>
            </Card>
          ) : (
            customers.map((customer) => (
              <Card key={customer.id} data-testid={`customer-item-${customer.id}`} className="border-[#E5E7EB] bg-white hover:border-[#FF5A36] transition-colors">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-orange-50 p-3 rounded-full">
                      <User className="h-6 w-6 text-[#FF5A36]" weight="duotone" />
                    </div>
                    <div>
                      <CardTitle className="font-['Chivo'] text-lg font-bold tracking-tight">{customer.name}</CardTitle>
                      <p className="text-sm text-[#6B7280]">{customer.mobile}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {customer.email && <p className="text-sm text-[#6B7280] mb-3">{customer.email}</p>}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-[#6B7280]">Total Business</span>
                      <span className="text-sm font-bold text-[#FF5A36]">{formatCurrency(customer.total_business)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-[#6B7280]">Orders</span>
                      <span className="text-sm font-medium text-[#121212]">{customer.order_count}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-[#6B7280]">Since</span>
                      <span className="text-sm font-medium text-[#121212]">{formatDate(customer.created_at)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomersPage;